import { MobileNumberField } from '@components/ui/MobileNumberField';
import { FormSkeleton } from '@components/ui/skeleton';
import { EditOutlined } from '@mui/icons-material';
import { Box, Button, CircularProgress, IconButton, TextField, Typography } from '@mui/material';
import { useAppShellStore } from '@store/appShellStore';
import { useEffect, useState } from 'react';

import { CANCEL_BTN, CARD, FIELD, FIELD_DISABLED, LABEL, PENCIL_BTN, SAVE_BTN, TITLE } from '../styles';
import { useMainCentreList, useUpdateMainCentre } from '../useSettingsData';

const EMPTY = {
  instituteName: '',
  addressLine1: '',
  addressLine2: '',
  addressLine3: '',
  phoneNumber: '',
  email: '',
  gstin: '',
  cin: '',
  sacCode: '',
};

type HeaderForm = typeof EMPTY;

const FIELDS: { key: keyof HeaderForm; label: string; placeholder?: string }[] = [
  { key: 'instituteName', label: 'Institute Name' },
  { key: 'addressLine1',  label: 'Address Line 1' },
  { key: 'addressLine2',  label: 'Address Line 2' },
  { key: 'addressLine3',  label: 'Address Line 3 (City, State, PIN)' },
  { key: 'phoneNumber',   label: 'Phone Number', placeholder: '10-digit phone number' },
  { key: 'email',         label: 'Email Address', placeholder: 'e.g. info@shriji.edu.in' },
  { key: 'gstin',         label: 'GSTIN' },
  { key: 'cin',           label: 'CIN Number (Optional)', placeholder: 'Enter CIN' },
  { key: 'sacCode',       label: 'SAC Code (Optional)', placeholder: 'Enter SAC code' },
];

/**
 * The complete receipt header for the CURRENT MAIN CENTRE — legal name, address,
 * contact and tax registration.
 *
 * One card rather than two, because all of it is one thing: a single GSTIN means
 * a single registered entity, so every centre in the city bills under the same
 * details. There is no per-centre version to reconcile against.
 *
 * Editing changes what future receipts print; ones already issued keep the
 * values snapshotted at the time, until their PDF is re-rendered.
 */
export const InstituteAndTaxCard = () => {
  const mainCentreId = useAppShellStore((s) => s.mainCentreId);
  const { data: mainCentres, isLoading } = useMainCentreList();
  const mainCentre = (mainCentres ?? []).find((m) => m._id === mainCentreId);
  const updateMainCentre = useUpdateMainCentre(mainCentreId);

  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<HeaderForm>(EMPTY);

  const fromMainCentre = (): HeaderForm => ({
    instituteName: mainCentre?.instituteName ?? '',
    addressLine1:  mainCentre?.addressLine1  ?? '',
    addressLine2:  mainCentre?.addressLine2  ?? '',
    addressLine3:  mainCentre?.addressLine3  ?? '',
    phoneNumber:   mainCentre?.phoneNumber   ?? '',
    email:         mainCentre?.email         ?? '',
    gstin:         mainCentre?.gstin         ?? '',
    cin:           mainCentre?.cin           ?? '',
    sacCode:       mainCentre?.sacCode       ?? '',
  });

  useEffect(() => {
    if (mainCentre) setForm(fromMainCentre());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainCentre]);

  const handleChange =
    (key: keyof HeaderForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const reset = () => {
    setForm(fromMainCentre());
    setIsEditing(false);
  };

  if (isLoading) return <FormSkeleton fields={9} withActions={false} />;

  return (
    <Box sx={CARD}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', mb: '20px' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={TITLE}>Institute &amp; Tax Details</Typography>
          <Typography sx={(theme) => ({ fontSize: 13, color: theme.palette.grey[500], mt: '4px' })}>
            The header printed on every receipt from {mainCentre?.name ?? 'this main centre'} — shared
            by all of its centres.
          </Typography>
        </Box>
        <IconButton
          size="small"
          disabled={!mainCentreId}
          onClick={() => setIsEditing(true)}
          sx={(theme) => ({ ...PENCIL_BTN, ...(isEditing ? { color: theme.palette.primary.main, bgcolor: theme.palette.status.info.bg } : {}) })}
        >
          <EditOutlined sx={{ fontSize: 17 }} />
        </IconButton>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: '16px', mb: '20px' }}>
        {FIELDS.map(({ key, label, placeholder }) => (
          <Box key={key}>
            <Typography component="label" sx={LABEL}>{label}</Typography>
            {key === 'phoneNumber' ? (
              <MobileNumberField
                value={form[key]}
                onChange={handleChange(key)}
                disabled={!isEditing}
                fullWidth
                placeholder={placeholder}
                sx={isEditing ? FIELD : FIELD_DISABLED}
                slotProps={{ htmlInput: { style: { paddingLeft: 12, paddingRight: 12 } } }}
              />
            ) : (
              <TextField
                value={form[key]}
                onChange={handleChange(key)}
                disabled={!isEditing}
                fullWidth
                placeholder={placeholder}
                sx={isEditing ? FIELD : FIELD_DISABLED}
                slotProps={{ htmlInput: { style: { paddingLeft: 12, paddingRight: 12 } } }}
              />
            )}
          </Box>
        ))}
      </Box>

      {isEditing && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <Button variant="outlined" sx={CANCEL_BTN} onClick={reset}>Cancel</Button>
          <Button
            onClick={() => updateMainCentre.mutate(form, { onSuccess: () => setIsEditing(false) })}
            disabled={updateMainCentre.isPending}
            sx={SAVE_BTN}
          >
            {updateMainCentre.isPending ? <CircularProgress size={16} color="inherit" /> : 'Save Changes'}
          </Button>
        </Box>
      )}
    </Box>
  );
};
